/**
 * SWATID-16 Service - Irrigo Platform
 * API REST /v1/swatid/controllers — el backend traduce a MQTT
 */

import { api, ApiResponse } from './api';
import { normalizeSwatidRelay, normalizeSwatidRelays } from '@/utils/swatidRelay';
import type {
  ApiSwatidController,
  ApiSwatidRelay,
  ApiSwatidStatusResponse,
  ApiSwatidSchedulesResponse,
  ApiSwatidSchedule,
  ApiSwatidDeviceInfo,
  ApiSwatidMessageResponse,
  ApiSwatidCachedResponse,
  CreateSwatidControllerRequest,
  UpdateSwatidControllerRequest,
  SwatidRelayActionRequest,
  SwatidRelayActionResponse,
  UpdateSwatidRelayConfigRequest,
  SetSwatidModeRequest,
  CreateSwatidScheduleRequest,
  ApiSwatidEventsResponse,
  ApiSwatidRelayHistoryResponse,
  ApiSwatidEventsCompareResponse,
  ApiSwatidSupervisionResponse,
  SetSwatidSupervisionRequest,
  SwatidEventsQueryParams,
  SwatidCompareQueryParams,
} from '@/types/api';

export const swatidService = {
  async create(data: CreateSwatidControllerRequest): Promise<ApiResponse<ApiSwatidController>> {
    return api.post<ApiSwatidController>('/swatid/controllers', data);
  },

  async update(
    id: string,
    data: UpdateSwatidControllerRequest
  ): Promise<ApiResponse<ApiSwatidController>> {
    return api.put<ApiSwatidController>(`/swatid/controllers/${id}`, data);
  },

  async getStatus(
    controllerId: string
  ): Promise<ApiSwatidCachedResponse<ApiSwatidStatusResponse>> {
    return api.get<ApiSwatidStatusResponse>(`/swatid/controllers/${controllerId}/status`);
  },

  async getInfo(controllerId: string): Promise<ApiSwatidCachedResponse<ApiSwatidDeviceInfo>> {
    return api.get<ApiSwatidDeviceInfo>(`/swatid/controllers/${controllerId}/info`);
  },

  async getRelays(controllerId: string): Promise<ApiResponse<ApiSwatidRelay[]>> {
    const res = await api.get<ApiSwatidRelay[]>(`/swatid/controllers/${controllerId}/relays`);
    if (!res.success || !res.data) return res;
    return { ...res, data: normalizeSwatidRelays(res.data) };
  },

  async refreshRelays(controllerId: string): Promise<ApiResponse<ApiSwatidMessageResponse>> {
    return api.post<ApiSwatidMessageResponse>(
      `/swatid/controllers/${controllerId}/relays/refresh`
    );
  },

  async controlRelay(
    controllerId: string,
    relayNumber: number,
    data: SwatidRelayActionRequest
  ): Promise<ApiResponse<SwatidRelayActionResponse>> {
    return api.post<SwatidRelayActionResponse>(
      `/swatid/controllers/${controllerId}/relays/${relayNumber}`,
      data
    );
  },

  async updateRelayConfig(
    controllerId: string,
    relayNumber: number,
    data: UpdateSwatidRelayConfigRequest
  ): Promise<ApiResponse<ApiSwatidRelay>> {
    const res = await api.put<ApiSwatidRelay>(
      `/swatid/controllers/${controllerId}/relays/${relayNumber}/config`,
      data
    );
    if (!res.success || !res.data) return res;
    return { ...res, data: normalizeSwatidRelay(res.data as unknown as Record<string, unknown>) };
  },

  /** Actualizar solo el nombre (PUT parcial según API_SWATID.md) */
  async updateRelayName(
    controllerId: string,
    relayNumber: number,
    name: string
  ): Promise<ApiResponse<ApiSwatidRelay>> {
    return this.updateRelayConfig(controllerId, relayNumber, { name: name.trim() });
  },

  async setMode(
    controllerId: string,
    data: SetSwatidModeRequest
  ): Promise<ApiResponse<{ mode: string }>> {
    return api.post<{ mode: string }>(`/swatid/controllers/${controllerId}/mode`, data);
  },

  async getTime(controllerId: string): Promise<ApiResponse<ApiSwatidMessageResponse>> {
    return api.get<ApiSwatidMessageResponse>(`/swatid/controllers/${controllerId}/time`);
  },

  async getNetwork(controllerId: string): Promise<ApiResponse<ApiSwatidMessageResponse>> {
    return api.get<ApiSwatidMessageResponse>(`/swatid/controllers/${controllerId}/network`);
  },

  async getSchedules(controllerId: string): Promise<ApiResponse<ApiSwatidSchedulesResponse>> {
    return api.get<ApiSwatidSchedulesResponse>(`/swatid/controllers/${controllerId}/schedules`);
  },

  async createSchedule(
    controllerId: string,
    data: CreateSwatidScheduleRequest
  ): Promise<ApiResponse<ApiSwatidSchedule>> {
    return api.post<ApiSwatidSchedule>(`/swatid/controllers/${controllerId}/schedules`, data);
  },

  async deleteSchedule(
    controllerId: string,
    scheduleId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(
      `/swatid/controllers/${controllerId}/schedules/${scheduleId}`
    );
  },

  async syncSchedules(controllerId: string): Promise<ApiResponse<ApiSwatidMessageResponse>> {
    return api.post<ApiSwatidMessageResponse>(
      `/swatid/controllers/${controllerId}/schedules/sync`
    );
  },

  async getEvents(
    controllerId: string,
    params?: SwatidEventsQueryParams
  ): Promise<ApiResponse<ApiSwatidEventsResponse>> {
    const query: Record<string, string | number> = {};
    if (params?.limit != null) query.limit = params.limit;
    if (params?.offset != null) query.offset = params.offset;
    if (params?.relay != null) query.relay = params.relay;
    if (params?.type) query.type = params.type;
    if (params?.date) query.date = params.date;
    return api.get<ApiSwatidEventsResponse>(`/swatid/controllers/${controllerId}/events`, query);
  },

  async getRelayHistory(
    controllerId: string,
    relayNumber: number,
    limit = 50
  ): Promise<ApiResponse<ApiSwatidRelayHistoryResponse>> {
    return api.get<ApiSwatidRelayHistoryResponse>(
      `/swatid/controllers/${controllerId}/relays/${relayNumber}/history`,
      { limit }
    );
  },

  async compareEvents(
    controllerId: string,
    params?: SwatidCompareQueryParams
  ): Promise<ApiResponse<ApiSwatidEventsCompareResponse>> {
    const query: Record<string, string | number> = {};
    if (params?.date) query.date = params.date;
    if (params?.relay != null) query.relay = params.relay;
    return api.get<ApiSwatidEventsCompareResponse>(
      `/swatid/controllers/${controllerId}/events/compare`,
      query
    );
  },

  async getSupervision(
    controllerId: string
  ): Promise<ApiResponse<ApiSwatidSupervisionResponse>> {
    return api.get<ApiSwatidSupervisionResponse>(
      `/swatid/controllers/${controllerId}/supervision`
    );
  },

  async setSupervision(
    controllerId: string,
    data: SetSwatidSupervisionRequest
  ): Promise<ApiResponse<ApiSwatidSupervisionResponse>> {
    return api.put<ApiSwatidSupervisionResponse>(
      `/swatid/controllers/${controllerId}/supervision`,
      data
    );
  },
};

export default swatidService;
